import { Component, OnInit, Input } from '@angular/core';
import { InfoService } from '@app/core/info/info.service';
import { TranslateService } from '@ngx-translate/core';
import { ActivationData, ActivationStatusStep, SmartCardData, SignedContent, BissSignedContent, BTrustIssuedCertificate } from '../activation.model';
import { ActivationService } from '../services/activation.service';
import { BissManager } from '../services/biss-manager';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { Observable, forkJoin, Subject } from 'rxjs';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'cloud-documents',
  templateUrl: './documents.component.html',
  styleUrls: ['./documents.component.scss']
})
export class DocumentsComponent implements OnInit {

  @Input()
  activationData: ActivationData[];
  smartCardData: SmartCardData;
  contractID;
  requestID;
  pppID;
  btrustMailActive;
  checkDocumentLabel: string;
  serviceRequestId;
  agentEmail: string;
  orderNumber;
  issuedCertificate: BTrustIssuedCertificate;
  smartCardValid: boolean = false;
  contentForSignArray: SignedContent[] = new Array<SignedContent>();
  uuid: string;
  bissDigestRequest: Array<any>[] = new Array<any>();
  documentsToUpload;
  documentFileNames = new Map<string, string>();
  alreadySigned;
  isLoading = false;

  private unsubscribe$: Subject<void> = new Subject<void>();
  btrustMail: any;

  constructor(  
    public info: InfoService,
    private translateService: TranslateService,
    private activationService: ActivationService,
    private bissManager: BissManager,
    private router: ActivatedRoute) { 
      
    this.uuid = this.router.snapshot.params['uuid'];
  }
  
  ngOnInit() {
    this.extractDocumentNumbers();
    this.checkDocumentLabel= this.translateService.instant('activation.documents.info');
    if (this.activationData[0].status === null) {
      this.setActivationStatus();
    }
    if(this.activationData[0].status === "SUCCESSFULLY_SIGN_DOCUMENTS"){
     this.alreadySignedStep();
    }
    this.serviceRequestId = this.activationData[0].serviceRequestId;
    this.agentEmail = this.activationData[0].agentEmail;
    this.orderNumber = this.activationData[0].orderNumber;
    this.checkBtrustMailActive(this.serviceRequestId);
    this.getSmartCardData();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private extractDocumentNumbers() {
    this.activationData.forEach(element => {
      if (element.documentType == 'CONTRACT') {
        this.contractID = element.documentId;
      }
      if (element.documentType == 'REQUEST') {
        this.requestID = element.documentId;
      }
      if (element.documentType == 'PP_PROTOCOL') {
        this.pppID = element.documentId;
      }
    });
  }

  previewDocument(documentId) {
    this.activationService.getDocument(documentId).subscribe(res => {
      if (res.status == 200) {
        const file = new Blob([res.body], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = fileURL;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
      } else {
        this.info.error(this.translateService.instant('info.error'));
      }
    });
  }

  certWrittenSteps() {
    this.isLoading = true;
    this.bissDigestRequest = []
    let observables: Observable<any>[] = [];
    this.activationData.forEach(activationData => {
    let obs = this.activationService.getDocumentFull(activationData.documentId).pipe(map((res:any) => {
      if (res.detailedSignedContent.type != 'BTRUST_MAIL') {
        this.documentFileNames.set(res.detailedSignedContent.id.toString(), res.detailedSignedContent.filename);
      }
        let request: any = {
          filename: res.detailedSignedContent.filename,
          content: res.detailedSignedContent.signedDocument,
          digest: true
        }
        return request;
      }))
      observables.push(obs);
    });
    
  forkJoin(
      observables
    ).subscribe(res => {
      res.forEach(r => {
        this.bissDigestRequest.push(r);
      });



      this.info.notice(this.translateService.instant('activation.documents.signDocsMessage'))
      return this.bissManager.getSigner().pipe(switchMap((res:any)  => {
          const userCertificate = this.bissManager.getUsedCert();
        const digestRequest = {
          data: this.bissDigestRequest,
          certificate: userCertificate
        }
        return this.activationService.bissDigestDocuments(digestRequest).pipe(switchMap((res:any) => {
          this.contentForSignArray = [];
          if (res.status === 200) {
            let signedContents: any[] = res.data.signedContents;
            signedContents.forEach(content => {
              let contentForSign: SignedContent = new SignedContent();
              contentForSign.originalContent = content.originalContent;
              contentForSign.digestTime = content.digestTime;
              contentForSign.signedContentCert = content.signingCert;
              contentForSign.signedContent = content.signedData;
              contentForSign.content = content.data;
              contentForSign.filename = content.filename;
              this.contentForSignArray.push(contentForSign);
            })
            return this.bissManager.bissSign(this.contentForSignArray).pipe(switchMap((res:BissSignedContent[]) => {
              if (res != null) {
                const contentsArr = res.map(r =>
                  ({ content: r.originalContent,
                    signature: r.signature,
                    certificate: r.certificate,
                    filename: r.filename,
                    digestContent: r.content,
                    digestTime: r.digestTime})
                );
                const body = { content: contentsArr };
                return this.activationService.composeDocuments(body).pipe(switchMap((res:any) => {
                  if (res.status === 200) {
                    this.documentsToUpload = res.documents;
                    this.saveSignedDocuments();
                    return res;
                  } else {
                    this.isLoading = false;
                    this.info.error(this.translateService.instant('info.error'));
                  }
                }));
              } else {
                this.isLoading = false;
                this.info.error(this.translateService.instant('info.error'));
              }
            }))
          } else {
            this.isLoading = false;
          };
        }))
      })).subscribe();
    })
  }




  getSmartCardData() {
    this.activationService.getSmartCardData(this.serviceRequestId)
      .subscribe(res => {
        if (res.status == 200) {
          this.smartCardData = res.scData;
        } else {
          this.info.error(this.translateService.instant('info.error'));
        }
      });
  }
  getIssuedCertificate() {
    this.activationService.getIssuedCert(this.serviceRequestId).subscribe(res => {
      if (res.status === 200) {
        this.issuedCertificate = {"certificate" : res.certificate, "valid" : res.valid};
       
      } else {
        this.info.error(this.translateService.instant('info.error'));
      }
    })
  }

  activateCertificate() {
    switch(this.activationData[0].status) {
      case ActivationStatusStep.SUCCESSFULLY_CHANGE_PIN_PUK: {
        this.pinPukChangedSteps();
        break;
      }
      case ActivationStatusStep.CERTIFICATE_ISSUED: {
        this.pinPukChangedSteps();
        break;
      }
      case ActivationStatusStep.ORDER_CONSUMED: {
        this.pinPukChangedSteps();
        break;
      }
      case ActivationStatusStep.INVOICE_CREATED: {
        this.invoiceCreatedSteps();
        break;
      }
      case ActivationStatusStep.CERTIFICATE_WRITTEN: {
        this.certWrittenSteps();
        break;
      }
      case ActivationStatusStep.REJECT_SIGN_DOCUMENTS: {
        this.certWrittenSteps();
        break;
      }
      case ActivationStatusStep.SUCCESSFULLY_SIGN_DOCUMENTS: {
        this.alreadySignedStep();
        break;
      }
    }

  }
  pinPukChangedSteps() {
    this.info.notice(this.translateService.instant('activation.documents.activateCertificateMessage'))
    this.isLoading = true;
    const orderNumbers: string[] = [this.orderNumber];
    let invoiceRequest = {
      agentEmail: this.agentEmail,
      orderNumbers: orderNumbers
    }
    this.activationService.activateCertificate(invoiceRequest, this.serviceRequestId, this.activationData[0].status).subscribe(res => {
      if (res.status === 200) {
         if(this.btrustMail && !this.btrustMailActive){
            this.activateBTrustEmail({"serviceRequestId" : this.serviceRequestId,"valid": ""});
          }
        this.activationService.getActivationData(this.uuid).subscribe(res => {
          this.activationData = res.data;
          this.extractDocumentNumbers();
          this.previewDocument(this.pppID);
          this.invoiceCreatedSteps();
        })
      } else {
        this.isLoading = false;
        this.info.error(this.translateService.instant('activation.documents.error'));
      }
    });
  }

  invoiceCreatedSteps() {
    this.isLoading = true;
    this.activationService.getIssuedCert(this.serviceRequestId).pipe(switchMap((res:any) => {
      if (res.status === 200) {
        //set certificate
        this.issuedCertificate = { "certificate": res.certificate, "valid": res.valid };

        //create request
        const jsonRequest = {
          vendorName: this.smartCardData.csp,
          serNum: this.smartCardData.cardSerialNumber,
          pemData: this.issuedCertificate.certificate
        }

        //write certificate to smart card
        this.info.notice(this.translateService.instant('activation.documents.writeCertScMessage'))
        return this.bissManager.writeCertToSc(jsonRequest).pipe(switchMap((res:any) => {
          if (res != null) {
            this.activationService.updateStatus(this.serviceRequestId, ActivationStatusStep.CERTIFICATE_WRITTEN).subscribe();
            this.certWrittenSteps();
            return res;
          } else {
            this.isLoading = false;
            this.info.error(this.translateService.instant('activation.documents.bissError'))
          }
        }))
      } else {
        this.isLoading = false;
        this.info.error(this.translateService.instant('info.error'));
      }
    })).subscribe()
  }

  saveSignedDocuments() {
    this.isLoading = true;
    let signedDocs = [];
    for (let i=0; i<this.documentsToUpload.length; i++) {
      const object = {
        content: this.documentsToUpload[i].content,
        // filename: this.documentFileNames.get(this.activationData[i].documentId),
        filename: this.documentsToUpload[i].filename,
        documentType: this.activationData[i].documentType
      }
      signedDocs.push(object);
    }
    this.activationService.uploadSignedDocuments(signedDocs, this.activationData[0].serviceRequestId).subscribe(res => {
      signedDocs = null;
      this.isLoading = false;
      if(res.status === 200){
        this.alreadySignedStep();
      }
    })
  }

  checkBtrustMailActive(serviceRequestId: string) : void {
    this.isLoading = true
   this.activationService.getBTrustEmailData(serviceRequestId).pipe(takeUntil(this.unsubscribe$)).subscribe(r=>{
    if(r.status === 200){
      this.btrustMailActive = r.data.active
      this.btrustMail = r.data.username;
      this.isLoading = false
    }else{
      this.isLoading = false;
    }});
  }

  previewBtrustMailDocument(btrustRequestId: string)  {
    this.activationService.previewBtrustMailDocument(this.btrustMail,btrustRequestId).pipe(takeUntil(this.unsubscribe$)).subscribe(res  => {
      if (res.status === 200) {
        const file = new Blob([res.body], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL)
      } else {
        this.info.error(this.translateService.instant('info.error'));
      }
    })
  }

  activateBTrustEmail(request: any) : void {
    this.activationService.getIssuedCert(request.serviceRequestId).subscribe(res=>{
      if(res.status === 200){
        request.valid = res.valid;
        this.activationService.activateBTrustEmail(request)
        .pipe(
          takeUntil(this.unsubscribe$)
          ).subscribe(res=>{
            if(res.status !== 200){
              this.info.error(this.translateService.instant('info.error'));
            }
          });
      }
    })
  }

  alreadySignedStep() {
    this.alreadySigned = true;
    this.info.notice(this.translateService.instant('activation.documents.alreadySigned'))
  }

  setActivationStatus() {
    this.activationService.getActivationData(this.uuid).subscribe((r)=>{
      this.activationData = r.data; 
    })
  }

}
